import React, { useContext, useRef } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { Loader } from "@culturehq/components";

import { Media, Organization, Story } from "chq";
import { useProfile } from "app/context";
import { isRTX } from "app/utils";

import { StoryModalContext } from "components/stories/video-studio/contexts/StoryContext";
import StoryModal from "components/stories/video-studio/StoryModal";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { moveElementToTrack } from "components/stories/video-studio/timeline/elements/utils";
import { addStoryToMontageTemplate, createElement, deleteElement, validImageUrl } from "components/stories/video-studio/utils/creatomate/utils";
import { IMAGE_SLIDE_DURATION, IMAGE_TYPE, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import StoriesTitle from "./StoriesTitle";
import EmptyStories from "./EmptyStories";
import StoriesGrid from "./StoriesGrid";
import getMontageVideoUrl from "../template/implementors/utils/getMontageVideoUrl";

interface StoriesProps {
  preview: Preview;
}

const Stories: React.FC<StoriesProps> = ({ preview }) => {
  const { profile: { organization } } = useProfile();
  const rootRef = useRef<HTMLDivElement>(null);
  const { fetching, onSelectStories, stories } = useContext(StoryModalContext);

  const { data } = useContext(CreatomateContext);
  const { onStoryUpdate, selectedTemplate } = data;
  const { state } = preview;

  const addElement = async (
    media: Media,
    storyTime: number,
    story: Story,
    storyTrack: number,
    duration: number,
    mediaWidth: number
  ): Promise<string | undefined> => {
    const url = media.mediaType === "video" ? getMontageVideoUrl(media, organization as Organization) : await validImageUrl(media);

    return createElement({
      type: media.mediaType,
      name: `${story.creator.name}'s ${media.mediaType}`,
      source: url,
      time: storyTime,
      fit: "contain",
      track: storyTrack === 0 ? undefined : storyTrack,
      duration,
      width: `${mediaWidth}%`
    }, preview);
  };

  const storyUpdateHandler = (story: Story, elementId?: string) => {
    const storyData = { ...story, creatomateId: elementId, selected: true };
    onSelectStories([storyData], { append: true });
    if (onStoryUpdate) onStoryUpdate(storyData);
  };

  const addMediaElementsToStory = async (story: Story, timelineElements: ElementState[]) => {
    const visuals = story.visuals || [];
    let storyTime = timelineElements.length === 0 ? 0 : state?.duration || 0;

    for (let index = 0; index < visuals.length; index += 1) {
      const media = visuals[index];
      const storyIndex = timelineElements.findIndex(element => element.source.type === "video" || element.source.type === "image");
      const storyTrack = storyIndex === -1 ? 1 : timelineElements[storyIndex].track;
      const duration = media.mediaType === "video" ? media.duration || 0 : IMAGE_SLIDE_DURATION;
      const heightProportion = (media.height || 1) / (state?.height || 1);

      // <renderer.state.duration> is used instead of <state.duration>
      // because the second one is not updated live and gives bad behavior
      // and the multi images are collapsed

      const mediaWidth = (100 * ((media.width || 1) / heightProportion)) / (state?.width || 1);
      // eslint-disable-next-line no-await-in-loop
      const elementId = await addElement(media, storyTime, story, storyTrack, duration, mediaWidth);
      const newElement = preview.getElements().find(el => el.source.id === elementId);

      if (storyTrack === 0) moveElementToTrack(-1, preview, newElement);

      // Update the story with the latest elementId
      if (index === visuals.length - 1) {
        storyUpdateHandler(story, elementId);
      }
      storyTime += duration;
    }
  };

  const handleSelectStory = async (story: Story) => {
    // TODO: we should get just elements that are part of a media of a story
    const timelineElements = preview.getElements().filter(element => (
      element.source.type === IMAGE_TYPE || element.source.type === VIDEO_TYPE
    ));
    if (selectedTemplate) {
      const elementId = await addStoryToMontageTemplate(
        selectedTemplate, preview, story, organization as Organization
      );
      storyUpdateHandler(story, elementId);
    } else {
      await addMediaElementsToStory(story, timelineElements);
    }
  };

  return (
    <div ref={rootRef}>
      <StoryModal onSelectStory={handleSelectStory} />
      <StoriesTitle />
      {fetching ? (
        <Loader loading />
      ) : (
        <>
          {stories?.length === 0 ? (
            <EmptyStories />
          ) : (
            <StoriesGrid
              onSelectStory={handleSelectStory}
              rootRef={rootRef}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Stories;
